import { CircularProgress, Grid, IconButton, MenuItem, TextField } from '@material-ui/core'
import { Button, CustomTooltip } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { getAdminMonthlySubscriptions, getUsersDetails } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { AutoSizer, Column, SortDirection, SortDirectionType, Table } from 'react-virtualized'
import { GAME_NAMES, OUTSETA_STAGES, User } from 'shared'
import { Filter } from '../stockPricing/FilterDock/Filter'
import { getFormattedDate, listRowsPerPage } from './BackofficePresenter'
import { UserDetailsDrawer } from './UserDetailsDrawer'
import { V2MigrationCsvCheckDialog } from './V2MigrationCsvCheckDialog'

// The Outseta stage alone doesn't say what the user can do (Free users stay "Trialing")
const SUBSCRIPTION_HINTS: Record<string, string> = {
  'Full - Trial': 'First 7 days after sign-up: everything unlocked',
  Free: 'Can use Powertools: branded articles, 50 publications/day, no premium features',
  No: 'Blocked: Free plan without branding consent, or expired',
}

export interface UserSearchProps {
  checkedUserId: undefined | string
  setCheckedUserId: (userId: string | undefined) => void
}

export const UserSearch = ({ checkedUserId, setCheckedUserId }: UserSearchProps): JSX.Element => {
  // const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [searchedUser, setSearchedUser] = useState('')
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [usersResults, setUsersResults] = useState<User[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [csvCheckOpen, setCsvCheckOpen] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(listRowsPerPage[0])
  const [totalPages, setTotalPages] = useState<number>()
  const [sortBy, setSortBy] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerData, setDrawerData] = useState<any[] | null>(null)
  const [drawerUserId, setDrawerUserId] = useState<string>('')

  const handleOpenDetails = async (e: React.MouseEvent, userId: string) => {
    e.stopPropagation()
    setDrawerUserId(userId)
    setDrawerOpen(true)
    setDrawerLoading(true)
    setDrawerData(null)
    try {
      const data = await getAdminMonthlySubscriptions(userId)
      setDrawerData(data)
    } catch (err) {
      enqueueSnackbar('Failed to load user details', { variant: 'error' })
    } finally {
      setDrawerLoading(false)
    }
  }

  const handleChangeSearchInput = (e: any) => {
    setSearchedUser(e.target.value)
  }

  const copyToClipboard = async (e: React.MouseEvent, value: string, label: string) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(`Failed to copy ${label}`, { variant: 'error' })
    }
  }

  const copyableCell = (value: string | undefined, label: string) => (
    <div className="copyable-cell">
      <span className="copyable-cell-text">{value}</span>
      {value && (
        <IconButton
          className="copyable-cell-copy"
          size="small"
          title={`Copy ${label}`}
          onClick={(e) => copyToClipboard(e, value, label)}
        >
          <i className="fa-light fa-copy" />
        </IconButton>
      )}
    </div>
  )

  // const getSubscriptionStatus = (subscriptionId: number) => {
  //   if (subscriptionId === SubscriptionId.No) {
  //     return t('subscription.noSubscription')
  //   } else if (subscriptionId === SubscriptionId.Trial) {
  //     return t('subscription.trialing')
  //   } else if (subscriptionId === SubscriptionId.Full) {
  //     return t('subscription.active')
  //   } else if (subscriptionId === SubscriptionId.FullCancelling) {
  //     return t('subscription.cancelling')
  //   } else if (subscriptionId === SubscriptionId.FullPastDue) {
  //     return t('subscription.pastDue')
  //   }
  // }

  // A new search (or row count) starts from the first page; only the arrows pass another one
  const handleSearchUser = async (page = 0) => {
    const query = searchedUser.trim()
    if (!query) {
      enqueueSnackbar('write a username or email of some user before searching', {
        variant: 'error',
      })
      return
    }
    setLoading(true)
    const params = {
      query,
      page,
      size: rowsPerPage,
    }
    try {
      const response = await getUsersDetails(params)
      setCurrentPage(page)
      setUsersResults(response.data)
      setTotalPages(Math.ceil(response.totalCount / rowsPerPage))
      setHasSearched(true)
    } catch (err) {
      enqueueSnackbar('Failed to search users', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // The tables below belong to the selected user: a new search must not leave them on the old one
  const handleNewSearch = () => {
    setCheckedUserId(undefined)
    handleSearchUser()
  }

  const toggleUser = (userId: string) => {
    setCheckedUserId(checkedUserId === userId ? undefined : userId)
  }

  const sort = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)
    let sortedUsers = [...usersResults]

    if (sortBy === 'userId') {
      sortedUsers = usersResults.sort((a, b) => {
        return a._id < b._id ? 1 : -1
      })
    } else if (sortBy === 'email') {
      sortedUsers = usersResults.sort((a, b) => {
        return a.username < b.username ? 1 : -1
      })
    } else if (sortBy === 'cmUsername') {
      sortedUsers = usersResults.sort((a, b) => {
        return (a.cardmarketUsername ?? '') < (b.cardmarketUsername ?? '') ? 1 : -1
      })
    } else if (sortBy === 'registrationDate') {
      sortedUsers = usersResults.sort((a, b) => {
        return a.createdAt < b.createdAt ? 1 : -1
      })
    }

    if (sortDirection === SortDirection.DESC) {
      sortedUsers = sortedUsers.reverse()
    }
    return sortedUsers
  }

  return (
    <div className="user-section mb-5">
      <h4 className="mb-3">User</h4>
      <Button variant="outlined" className="mb-3" onClick={() => setCsvCheckOpen(true)}>
        Check migration to v2 from a CSV
      </Button>
      <V2MigrationCsvCheckDialog open={csvCheckOpen} onClose={() => setCsvCheckOpen(false)} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={3}>
          <TextField
            data-testid="user-search-input"
            label="Search by username or email"
            value={searchedUser}
            onChange={handleChangeSearchInput}
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleNewSearch()
              }
            }}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={listRowsPerPage.map((row) => (
              <MenuItem key={row} value={row}>
                {row}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setRowsPerPage(newValue)}
            defaultValue={listRowsPerPage[0]}
            label="Rows"
            name={'rows'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Button data-testid="search-users-btn" variant="contained" onClick={handleNewSearch}>
            Search users
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">Loading</h6>
          <CircularProgress size={100} />
        </div>
      ) : usersResults.length === 0 ? (
        hasSearched && (
          <span className="my-5 d-block text-center fw-bold" style={{ fontSize: '1.1rem' }}>
            No search results found
          </span>
        )
      ) : (
        <>
          <div
            className="user-table-results my-5"
            style={{ height: Math.min(usersResults.length * 45 + 40 + 30, 450) }}
          >
            <span className="mb-1">{usersResults.length} rows</span>
            <AutoSizer>
              {({ width }) => (
                <Table
                  width={Math.max(width, 1300)}
                  height={Math.min(usersResults.length * 45 + 40, 400)}
                  headerHeight={40}
                  rowHeight={45}
                  rowCount={usersResults.length}
                  rowGetter={({ index }) => usersResults[index]}
                  className={`users-table`}
                  sort={sort}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onRowClick={({ rowData }) => toggleUser(rowData._id)}
                  rowStyle={({ index }: { index: number }) => ({
                    cursor: 'pointer',
                    backgroundColor:
                      index >= 0 && usersResults[index]?._id === checkedUserId
                        ? '#e3f2fd'
                        : undefined,
                  })}
                >
                  <Column
                    width={230}
                    flexGrow={0}
                    flexShrink={0}
                    label="User id"
                    dataKey="userId"
                    cellRenderer={({ rowData: user }) => copyableCell(user._id, 'User id')}
                  />

                  <Column
                    width={140}
                    flexGrow={1}
                    flexShrink={1}
                    label="CM username"
                    dataKey="cmUsername"
                    cellRenderer={({ rowData: user }) =>
                      copyableCell(user.cardmarketUsername, 'CM username')
                    }
                  />

                  <Column
                    width={240}
                    flexGrow={2}
                    flexShrink={1}
                    label="Email"
                    dataKey="email"
                    cellRenderer={({ rowData: user }) => copyableCell(user.username, 'Email')}
                  />

                  <Column
                    width={160}
                    flexGrow={1}
                    flexShrink={1}
                    label="Registration date"
                    dataKey="registrationDate"
                    cellRenderer={({ rowData: user }) => (
                      <div>{getFormattedDate(user.createdAt)}</div>
                    )}
                  />

                  <Column
                    width={100}
                    flexGrow={1}
                    flexShrink={1}
                    label="Currency"
                    dataKey="currency"
                    cellRenderer={({ rowData: user }) => <div>{user.currency}</div>}
                  />

                  <Column
                    width={180}
                    flexGrow={1}
                    flexShrink={1}
                    label="Subscription"
                    dataKey="subscriptionName"
                    cellRenderer={({ rowData: user }) => (
                      <div title={SUBSCRIPTION_HINTS[user.subscriptionName]}>
                        <div>{user.subscriptionName}</div>
                        <small className="text-muted">
                          Outseta: {OUTSETA_STAGES[user.outseta?.accountStage] ?? '-'}
                        </small>
                      </div>
                    )}
                  />

                  <Column
                    width={160}
                    flexGrow={1}
                    flexShrink={1}
                    label="Onboarding"
                    dataKey="onboarding"
                    disableSort
                    cellRenderer={({ rowData: user }: { rowData: User }) => (
                      <CustomTooltip
                        title={
                          <div>
                            <div>
                              Source: {user.completeOnboarding?.onboardingSourceSync || '-'}
                            </div>
                            <div>
                              Games:{' '}
                              {user.completeOnboarding?.finishedWritingPowertoolsTransactionsAt
                                .map((f) => GAME_NAMES[Number(f.idGame)] ?? f.idGame)
                                .join(', ') || '-'}
                            </div>
                          </div>
                        }
                      >
                        <div>{user.hasOnboarded ? 'Completed' : 'Not completed'}</div>
                      </CustomTooltip>
                    )}
                  />

                  <Column
                    width={100}
                    flexGrow={1}
                    flexShrink={1}
                    label="Version"
                    dataKey="version"
                    cellRenderer={({ rowData: user }) => (
                      <div>{user.isTester?.ptInventory ? <span>v2</span> : <span>v1</span>}</div>
                    )}
                  />

                  <Column
                    width={80}
                    flexGrow={0}
                    flexShrink={0}
                    label="Details"
                    dataKey="details"
                    disableSort
                    cellRenderer={({ rowData: user }) => (
                      <IconButton
                        size="small"
                        data-testid="open-user-details"
                        onClick={(e) => handleOpenDetails(e, user._id)}
                      >
                        <i className="fa-light fa-circle-info" />
                      </IconButton>
                    )}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
          <div className="mb-5">
            <TablePaginationControls
              currentPage={currentPage}
              setPage={handleSearchUser}
              isLastPage={currentPage + 1 >= (totalPages ?? 0)}
              totalPages={totalPages}
              startFromZero={true}
            />
          </div>
        </>
      )}
      <UserDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={drawerData}
        loading={drawerLoading}
        userId={drawerUserId}
        email={usersResults.find((u) => u._id === drawerUserId)?.username ?? ''}
        currency={usersResults.find((u) => u._id === drawerUserId)?.currency ?? 'EUR'}
        shopifyEnabled={
          usersResults.find((u) => u._id === drawerUserId)?.featureFlags?.shopify ?? false
        }
        taxationEnabled={
          usersResults.find((u) => u._id === drawerUserId)?.featureFlags?.taxation ?? false
        }
        isV2={usersResults.find((u) => u._id === drawerUserId)?.isTester?.ptInventory ?? false}
        cardtraderUserName={
          usersResults.find((u) => u._id === drawerUserId)?.assignedCardtraderUser?.username
        }
        onEmailUpdated={(newEmail) => {
          setUsersResults((prev) =>
            prev.map((u) => (u._id === drawerUserId ? { ...u, username: newEmail } : u))
          )
        }}
        onCurrencyUpdated={(currency) => {
          setUsersResults((prev) =>
            prev.map((u) => (u._id === drawerUserId ? { ...u, currency } : u))
          )
        }}
      />
    </div>
  )
}
